import {
  computed,
  ref,
  watch,
  onMounted,
  onBeforeUnmount,
  type Ref,
} from "vue";
import { useRoute, useRouter } from "vue-router";
import { useAuthenticationStore } from "@/store/authentication.store";
import { resolveCustomerOrgUuid } from "@/utils/navigation/customerOrgUuid";
import { storeToRefs } from "pinia";
import dayjs from "dayjs";
import { useSolutionsStore } from "@/store/solutions.store";
import { useAzureStore } from "@/store/azure.store";
import {
  getCallbackUrl,
  getApiClientUrl,
  getOldAppClientId,
  getNewAppClientId,
} from "@/config/runtimeConfig";
import { useFlag } from "@/composables/useFlags/useFlag";
import { $http } from "@/plugins/api";
import {
  PHISH_SERVICE,
  SAT_SERVICE,
  PHISHING_SIMULATION,
} from "@/utils/services";
import { useToastStore } from "@/components/TToasts/toast.store";
import { useI18n } from "vue-i18n";
import {
  readConsentResult,
  clearConsentResult,
  type ConsentResult,
} from "@/utils/consent";
import {
  AZURE_CALLBACK_PATH,
  NDR_CALLBACK_PATH,
  buildAppReturnUrl,
} from "@/utils/returnUrl";
import { isEmbedded } from "@/remote/contract";
import {
  openConsentPopup,
  parseConsentResult,
  waitForConsentPopup,
  type ConsentPopup,
} from "@/remote/consentPopup";

interface OnboardingStatusEntry {
  status: boolean;
  created_at: string;
  updated_at: string;
}

interface OnboardingStatusResponse {
  onboarding: OnboardingStatusEntry;
  journal_rule: OnboardingStatusEntry;
  ndr_address: OnboardingStatusEntry;
}

export function useConnectionsState() {
  const { t } = useI18n();
  const route = useRoute();
  // The consent popups must come back to the UI that opened them (same origin
  // and router base), so every flow tells the API where this UI lives.
  const router = useRouter();
  const authStore = useAuthenticationStore();
  const toastStore = useToastStore();
  const azureStore = useAzureStore();
  const { isOnLegacyApp, isOnNewApp, connectedAzureApp } =
    storeToRefs(azureStore);
  // The whole Azure app-migration surface on this page (new-app connect, the
  // permissions-update banner, the connection-status detection, and the
  // NDR/journal new-app gate) sits behind M365_AZURE_APP_MIGRATION. With the
  // flag off the page behaves exactly as it did before P-287: a plain old-app
  // connect and phish-service-only NDR/journal visibility. The separate
  // M365_AZURE_APP_CHOOSER flag still governs the in-card app chooser.
  const { isFeatureAvailable: isAppMigrationEnabled } = useFlag(
    "M365_AZURE_APP_MIGRATION",
  );

  const isRequestPending = ref(false);
  const pendingM365App = ref<"old" | "new" | null>(null);
  const m365UpdateStatus = ref<"idle" | "success" | "error">("idle");
  const isNdrRequestPending = ref(false);
  const isJournalRuleRequestPending = ref(false);
  const receivedMessage = ref();
  const timestamp = ref<string | null>(null);
  const callback = getCallbackUrl();
  const onboardingStatus = ref<OnboardingStatusResponse | null>(null);
  const ndrError = ref(false);
  const ndrErrorMessage = ref("");
  const journalRuleError = ref(false);
  const journalRuleErrorMessage = ref("");
  const initialLoadComplete = ref(false);
  const ndrConsentHandled = ref(false);
  const journalRuleConsentHandled = ref(false);

  const selectedCustomer = computed(() => authStore.selectedCustomer);
  const selectedAccount = computed(() => authStore.selectedAccount);
  const isM365Connected = computed(
    () => onboardingStatus.value?.onboarding.status ?? false,
  );
  const isNdrConfigured = computed(
    () => onboardingStatus.value?.ndr_address.status ?? false,
  );
  const isJournalRuleConfigured = computed(
    () => onboardingStatus.value?.journal_rule.status ?? false,
  );
  // Production has no VITE_CLIENT_ID_NEW yet (the prod Azure app is not
  // registered), so getNewAppClientId() is empty there. When that happens the
  // primary connect must fall back to the working old app instead of building a
  // consent URL with client_id=undefined; it auto-switches to the new app as
  // soon as the id is configured.
  const isNewAppConfigured = computed(() => !!getNewAppClientId());
  const primaryConnectApp = computed<"old" | "new">(() =>
    isAppMigrationEnabled.value && isNewAppConfigured.value ? "new" : "old",
  );
  // The migration ("Update permissions") banner only makes sense when there is
  // a new app to migrate to; suppress it entirely while the new app id is absent
  // or the migration flag is off.
  const needsAppUpdate = computed(
    () =>
      isAppMigrationEnabled.value &&
      isOnLegacyApp.value &&
      isNewAppConfigured.value,
  );

  const { solutions } = storeToRefs(useSolutionsStore());
  const isPhishServiceActive = computed(() =>
    solutions.value.some(
      (s: { solution_uuid: string }) => s.solution_uuid === PHISH_SERVICE,
    ),
  );
  // NDR and journal-rule setup require the new Azure app's permissions, so the
  // tiles and stepper steps stay hidden until the customer has updated their
  // permissions (i.e. connected to the new app). Being on the legacy app or
  // not connected at all both keep them hidden.
  const canShowNdrAndJournal = computed(
    () =>
      isPhishServiceActive.value &&
      (isAppMigrationEnabled.value ? isOnNewApp.value : true),
  );
  const isSatServiceActive = computed(() =>
    solutions.value.some(
      (s: { solution_uuid: string }) => s.solution_uuid === SAT_SERVICE,
    ),
  );
  const isPhsServiceActive = computed(() =>
    solutions.value.some(
      (s: { solution_uuid: string }) => s.solution_uuid === PHISHING_SIMULATION,
    ),
  );

  const formattedM365LastUpdate = computed(() => {
    const updatedAt = onboardingStatus.value?.onboarding.updated_at;
    if (!updatedAt) return "";
    return dayjs(updatedAt).format("YYYY/MM/DD, HH:mm");
  });

  const formattedNdrLastUpdate = computed(() => {
    const updatedAt = onboardingStatus.value?.ndr_address.updated_at;
    if (!updatedAt) return "";
    return dayjs(updatedAt).format("YYYY/MM/DD, HH:mm");
  });

  const formattedJournalRuleLastUpdate = computed(() => {
    const updatedAt = onboardingStatus.value?.journal_rule.updated_at;
    if (!updatedAt) return "";
    return dayjs(updatedAt).format("YYYY/MM/DD, HH:mm");
  });

  const channel = new BroadcastChannel("azure-connection");
  const ndrChannel = new BroadcastChannel("ndr-connection");

  watch(
    () => receivedMessage.value,
    async (newValue) => {
      const uuid = newValue;
      if (uuid && uuid.value?.data != null) {
        await azureStore.getAllAzureAccounts(uuid);
      }
    },
  );

  /**
   * EMBEDDED ONLY (see remote/consentPopup.ts). Waits for a tagged consent
   * popup and reports how it ended: the outcome parsed from the URL it
   * announced, whatever an earlier callback page persisted, or null when the
   * user closed it. The popup is closed by the time this resolves.
   */
  const awaitEmbeddedConsent = async (
    popup: ConsentPopup,
  ): Promise<ConsentResult | null> => {
    try {
      const outcome = await waitForConsentPopup(popup);
      const announced =
        outcome.kind === "redirect" ? parseConsentResult(outcome.url) : null;
      return announced ?? readConsentResult();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      return readConsentResult();
    } finally {
      clearConsentResult();
    }
  };

  // What happens once the M365 consent popup is gone, whichever way it went:
  // refresh the connection state, then report the outcome. `consentResult` is
  // the only reliable signal that admin consent actually completed: closing or
  // cancelling the popup leaves none, so success is never inferred from the
  // connection state alone.
  // Everything the M365 card and the SFI banner read: the accounts, the
  // connection status (which Azure app the customer is on; gated with the rest
  // of the migration surface behind the flag) and the onboarding status.
  const refreshM365State = async (uuid: string) => {
    await azureStore.getAllAzureAccounts(uuid);
    if (isAppMigrationEnabled.value) {
      await azureStore.getConnectionStatus(uuid);
    }
    await fetchOnboardingStatus(uuid);
  };

  // EMBEDDED ONLY. After a successful consent the API may still be finalising
  // the connection when the popup comes back: the onboarding status can lag
  // the redirect by a few seconds. (Standalone, the callback page counts down
  // 5 seconds before the popup closes, so this never applied there.) Re-check
  // it, briefly, before reporting, so the card does not sit on "connect"
  // until the next refresh.
  const CONNECTION_SETTLE_ATTEMPTS = 10;
  const CONNECTION_SETTLE_INTERVAL_MS = 1000;
  const waitForM365Connected = async (uuid: string) => {
    for (
      let attempt = 0;
      attempt < CONNECTION_SETTLE_ATTEMPTS && !isM365Connected.value;
      attempt++
    ) {
      await new Promise((resolve) =>
        setTimeout(resolve, CONNECTION_SETTLE_INTERVAL_MS),
      );
      await fetchOnboardingStatus(uuid);
    }
  };

  // What happens once the M365 consent popup is gone, whichever way it went:
  // refresh the connection state, then report the outcome. `consentResult` is
  // the only reliable signal that admin consent actually completed: closing or
  // cancelling the popup leaves none, so success is never inferred from the
  // connection state alone. A refresh that fails is logged and the outcome is
  // still reported, so the card can never stay stuck pending.
  const finishM365Consent = async (
    uuid: string,
    consentResult: ConsentResult | null,
    onClosed?: (result: ConsentResult | null) => void,
  ) => {
    // Embedded, the button (and its spinner, keyed on pendingM365App) stays
    // pending until the state has settled. Standalone releases it first, as it
    // always did.
    const settle = isEmbedded();
    if (!settle) {
      isRequestPending.value = false;
      pendingM365App.value = null;
    }
    try {
      await refreshM365State(uuid);
      if (settle && consentResult?.success && !isM365Connected.value) {
        await waitForM365Connected(uuid);
        // The accounts and connection status will have caught up with it too.
        if (isM365Connected.value) await refreshM365State(uuid);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    } finally {
      isRequestPending.value = false;
      pendingM365App.value = null;
    }
    if (onClosed) {
      onClosed(consentResult);
    } else if (consentResult?.success && isM365Connected.value) {
      toastStore.displayToast({
        title: t("success"),
        message: t("microsoft-365-connected"),
        duration: 5000,
        variant: "success",
        testId: "m365-connection-toast-success",
      });
    }
  };

  // Opens the M365 admin-consent popup for the given Azure app and, once the
  // popup closes, refreshes the connection state. `onClosed` lets a caller
  // (e.g. the permissions-update flow) react to the outcome instead of the
  // default "connected" success toast.
  const openM365Consent = (
    app: "old" | "new",
    onClosed?: (result: ConsentResult | null) => void,
  ) => {
    isRequestPending.value = true;
    pendingM365App.value = app;
    // Drop any stale consent result so a previous attempt cannot leak into this
    // one (the popup persists a fresh result to the same key on completion).
    clearConsentResult();

    // The org bound to the customer's M365 tenant (state.o) MUST be the customer
    // whose page this is — taken from the /customer/:orgUuid route, never from
    // selectedAccount (the logged-in System/MSP principal). Falling back to the
    // principal's org is what onboarded customers under the wrong organisation.
    const uuid = resolveCustomerOrgUuid(
      route.params.orgUuid,
      selectedCustomer.value?.uuid,
    );
    if (!uuid) {
      isRequestPending.value = false;
      pendingM365App.value = null;
      toastStore.displayToast({
        title: t("error"),
        message: t("generic-error"),
        duration: 5000,
        variant: "danger",
        testId: "m365-connection-toast-error",
      });
      // Keep the update/connect banner in sync when a caller drives it.
      onClosed?.(null);
      return;
    }

    // Name is a cosmetic label only; source it from the customer (never the
    // principal) so a missing customer context yields an empty label, not the
    // principal's name.
    const name = btoa(selectedCustomer.value?.name || "");
    const billingId = selectedCustomer.value?.billing_system_id;

    const clientId = app === "old" ? getOldAppClientId() : getNewAppClientId();

    const state = {
      v: billingId,
      o: uuid,
      n: name,
      c: clientId,
      t: timestamp.value,
      // Where the API sends the popup after consent; it must be on an origin
      // the API allows, otherwise the API falls back to its default frontend.
      r: buildAppReturnUrl(router, AZURE_CALLBACK_PATH),
    };

    const encodeObj = encodeURI(JSON.stringify(state));
    const strWindowFeatures =
      "location=yes,height=570,width=520,scrollbars=yes,status=yes";

    const URL = `https://login.microsoftonline.com/organizations/v2.0/adminconsent?client_id=${clientId}&redirect_uri=${callback}&state=${encodeObj}&scope=https://outlook.office365.com/.default`;

    if (isEmbedded()) {
      // EMBEDDED: the red-app flow (remote/consentPopup.ts). A blank, tagged
      // popup is pointed at the consent URL; the host announces where it came
      // back and the wait closes it.
      const popup = openConsentPopup({ placeholder: t("loading") });
      if (!popup) {
        // Blocked by the browser: nothing to wait for and nothing to refresh.
        // Reported once, the same way a missing org is.
        isRequestPending.value = false;
        pendingM365App.value = null;
        toastStore.displayToast({
          title: t("error"),
          message: t("generic-error"),
          duration: 5000,
          variant: "danger",
          testId: "m365-connection-toast-error",
        });
        onClosed?.(null);
        return;
      }
      popup.window.location.href = URL;
      void awaitEmbeddedConsent(popup)
        .then((result) => finishM365Consent(uuid, result, onClosed))
        .catch((error: unknown) => {
          // eslint-disable-next-line no-console
          console.error(error);
        });
      return;
    }

    const popupWindow = window.open(URL, "_blank", strWindowFeatures);

    const checkIfPopupWindowClosed = setInterval(async () => {
      if (popupWindow?.closed) {
        clearInterval(checkIfPopupWindowClosed);
        // The callback page persisted the outcome from the redirect URL before
        // the popup closed itself.
        const consentResult = readConsentResult();
        clearConsentResult();
        await finishM365Consent(uuid, consentResult, onClosed);
      }
    }, 500);
  };

  const onConnectM365 = (useOldApp = false) => {
    // The primary connect action targets the new Azure app whenever it is
    // configured; the legacy app is otherwise reachable through the explicit
    // chooser button (gated behind M365_AZURE_APP_CHOOSER, dev/test only). When
    // the new app id is absent (prod, until the app is registered) the primary
    // button falls back to the old app so it keeps working. Both old-app paths
    // are plain connects — no migration banner.
    if (useOldApp || primaryConnectApp.value === "old") {
      openM365Consent("old");
      return;
    }
    // Connecting to the new app mirrors the permissions-update flow: surface the
    // same in-card success/error banner instead of the default "connected"
    // toast. Green shows only when the consent redirect reported success —
    // closing the popup (no result) or an explicit failure shows the error
    // banner, even for an account that was never connected to the old app.
    m365UpdateStatus.value = "idle";
    openM365Consent("new", (result) => {
      m365UpdateStatus.value = result?.success ? "success" : "error";
    });
  };

  const onConnectOldAppM365 = () => onConnectM365(true);

  // The update action always targets the new Azure app (it is the whole point
  // of the migration) regardless of the chooser flag. Success/error is derived
  // from the consent redirect outcome, not the connection state: cancelling the
  // popup while already on the new app must not be read as a fresh success.
  const onUpdateM365Permissions = () => {
    m365UpdateStatus.value = "idle";
    openM365Consent("new", (result) => {
      m365UpdateStatus.value = result?.success ? "success" : "error";
    });
  };

  onMounted(async () => {
    try {
      isRequestPending.value = true;
      const uuid =
        selectedCustomer.value?.uuid || selectedAccount.value?.uuid || "";
      const now = new Date();
      timestamp.value = now.toISOString().replace("T", " ").split(".")[0];
      await azureStore.getAllAzureAccounts(uuid);
      // Needed so the card knows which Azure app the customer is on (old/new)
      // and can show the permissions-update banner and its buttons. It is
      // isolated in its own try/catch: connection-status is best-effort, and a
      // failure here must not hide the connected state (onboarding status) or
      // block initialLoadComplete. Gated behind the migration flag — before
      // P-287 the page never fetched connection-status here.
      if (isAppMigrationEnabled.value) {
        try {
          await azureStore.getConnectionStatus(uuid);
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error(e);
        }
      }

      await fetchOnboardingStatus(uuid);
      initialLoadComplete.value = true;
    } catch (e) {
      /* eslint-disable no-console */
      console.log(e);
    } finally {
      isRequestPending.value = false;
    }

    channel.onmessage = (event) => {
      receivedMessage.value = event.data;
    };

    ndrChannel.onmessage = async (event) => {
      const msg = event.data;
      if (isJournalRuleRequestPending.value) {
        if (msg.success) {
          if (journalRuleConsentHandled.value) return;
          journalRuleConsentHandled.value = true;
          journalRuleError.value = false;
          journalRuleErrorMessage.value = "";
          await configureJournalRule(resolveExchangeOrgUuid(), true);
        } else {
          journalRuleError.value = true;
          journalRuleErrorMessage.value =
            msg.errorDescription || msg.error || "";
          isJournalRuleRequestPending.value = false;
        }
      } else {
        if (msg.success) {
          if (ndrConsentHandled.value) return;
          ndrConsentHandled.value = true;
          ndrError.value = false;
          ndrErrorMessage.value = "";
          await configureNdr(resolveExchangeOrgUuid(), true);
        } else {
          ndrError.value = true;
          ndrErrorMessage.value = msg.errorDescription || msg.error || "";
          isNdrRequestPending.value = false;
        }
      }
    };
  });

  const fetchOnboardingStatus = async (organisationID: string) => {
    const res = await $http.get<OnboardingStatusResponse>(
      `${getApiClientUrl()}/v1/pt/onboarding/${organisationID}/status`,
    );
    onboardingStatus.value = res.data;
  };

  // The Exchange consent popup is redirected by the API to `return_url`, so
  // point it at this UI's own /ndr/callback page (origin, never a router base).
  const exchangeOnboardingUrl = (
    uuid: string,
    action: "journal-rule" | "ndr-address",
  ) => {
    const url = new URL(
      `${getApiClientUrl()}/v1/pt/onboarding/${uuid}/${action}`,
    );
    url.searchParams.set(
      "return_url",
      buildAppReturnUrl(router, NDR_CALLBACK_PATH),
    );
    return url.toString();
  };

  // EMBEDDED ONLY. The two Exchange consent flows (journal rule, NDR address)
  // differ only in their refs and requests; this is the part they share.
  type ExchangeConsentFlow = {
    handled: Ref<boolean>;
    error: Ref<boolean>;
    errorMessage: Ref<string>;
    pending: Ref<boolean>;
  };

  // How long to keep re-sending the completing request while the API still
  // asks for a consent that was just granted: it can lag the popup's return by
  // a few seconds, the same way the M365 status does.
  const CONSENT_PROPAGATION_ATTEMPTS = 5;
  const CONSENT_PROPAGATION_INTERVAL_MS = 1000;
  const consentPropagationDelay = () =>
    new Promise((resolve) =>
      setTimeout(resolve, CONSENT_PROPAGATION_INTERVAL_MS),
    );

  // Drives a tagged popup through an Exchange consent and reports what comes
  // next: "complete" when consent was granted (send the completing request),
  // "done" when the flow has ended here (an error is shown, or the user closed
  // the popup and the real status was re-read).
  const runEmbeddedExchangeConsent = async (
    popup: ConsentPopup,
    consentUrl: string,
    flow: ExchangeConsentFlow,
    uuid: string,
  ): Promise<"complete" | "done"> => {
    popup.window.location.href = consentUrl;
    const result = await awaitEmbeddedConsent(popup);
    // Defensive: a callback page may already have driven it (see the
    // ndr-connection channel handler).
    if (flow.handled.value) return "done";
    flow.handled.value = true;
    if (result?.success) return "complete";
    if (result) {
      flow.error.value = true;
      flow.errorMessage.value = result.errorDescription || result.error || "";
    } else {
      await fetchOnboardingStatus(uuid);
    }
    flow.pending.value = false;
    return "done";
  };

  // The browser refused the blank popup, or the API keeps asking for a consent
  // that was just granted: nothing more this flow can do by itself, and never
  // another popup.
  const failExchangeConsent = (flow: ExchangeConsentFlow) => {
    flow.error.value = true;
    flow.errorMessage.value = t("generic-error");
    flow.pending.value = false;
  };

  // The org an Exchange flow acts on, resolved when the admin clicks and
  // carried through the consent and the completing request: switching company
  // while the popup is open must not move that request to the new one.
  const resolveExchangeOrgUuid = () =>
    selectedCustomer.value?.uuid || selectedAccount.value?.uuid || "";

  const createJournalRule = (uuid: string) =>
    $http.post<{ url?: string }>(exchangeOnboardingUrl(uuid, "journal-rule"));

  // `afterConsent`: the request that completes a consent just granted. It
  // never needs a popup, so none is opened for it.
  const journalRuleFlow: ExchangeConsentFlow = {
    handled: journalRuleConsentHandled,
    error: journalRuleError,
    errorMessage: journalRuleErrorMessage,
    pending: isJournalRuleRequestPending,
  };

  // `afterConsent`: the request that completes a consent just granted. It
  // never needs a popup, so none is opened for it; `attempt` counts its
  // retries while the API has not yet seen that consent.
  const configureJournalRule = async (
    uuid: string,
    afterConsent = false,
    attempt = 0,
  ) => {
    if (!uuid) return;

    // Clear any error from a previous attempt up front (see configureNdr).
    journalRuleError.value = false;
    journalRuleErrorMessage.value = "";
    // Drop any stale consent result so it cannot leak into this attempt.
    clearConsentResult();
    isJournalRuleRequestPending.value = true;
    // EMBEDDED: open the (blank, tagged) popup BEFORE the request, while the
    // click still counts as user activation (remote/consentPopup.ts). Null
    // means the browser blocked it; the standalone code below is never the
    // fallback for that.
    const embedded = isEmbedded();
    const popup =
      embedded && !afterConsent
        ? openConsentPopup({ placeholder: t("loading") })
        : null;
    try {
      const res = await createJournalRule(uuid);
      const consentUrl = res.data?.url;

      if (consentUrl) {
        journalRuleConsentHandled.value = false;
        if (embedded) {
          if (afterConsent) {
            // Granted a moment ago but not yet visible to the API: try again
            // shortly, a few times.
            if (attempt < CONSENT_PROPAGATION_ATTEMPTS) {
              await consentPropagationDelay();
              await configureJournalRule(uuid, true, attempt + 1);
              return;
            }
            failExchangeConsent(journalRuleFlow);
            return;
          }
          if (!popup) {
            failExchangeConsent(journalRuleFlow);
            return;
          }
          const next = await runEmbeddedExchangeConsent(
            popup,
            consentUrl,
            journalRuleFlow,
            uuid,
          );
          if (next === "complete") await configureJournalRule(uuid, true);
          return;
        }
        const strWindowFeatures =
          "location=yes,height=570,width=520,scrollbars=yes,status=yes";
        const popupWindow = window.open(
          consentUrl,
          "_blank",
          strWindowFeatures,
        );

        const checkIfPopupWindowClosed = setInterval(async () => {
          if (!popupWindow?.closed) return;
          clearInterval(checkIfPopupWindowClosed);
          // The consent flow continues via the BroadcastChannel 'success'
          // message (which re-invokes this function). If the window was
          // closed without that message, do NOT re-open it. Re-check the real
          // state instead, in case consent succeeded but the broadcast was
          // lost — this is a read-only status fetch, so it cannot loop.
          if (journalRuleConsentHandled.value) return;
          journalRuleConsentHandled.value = true;
          try {
            const result = readConsentResult();
            if (result && result.success === false) {
              // The consent popup reported a failure. Surface it even when the
              // BroadcastChannel message never reached us (it can be lost when
              // the popup auto-closes right after posting).
              journalRuleError.value = true;
              journalRuleErrorMessage.value =
                result.errorDescription || result.error || "";
            } else {
              await fetchOnboardingStatus(uuid);
            }
          } catch (e) {
            console.error(e);
          } finally {
            clearConsentResult();
            isJournalRuleRequestPending.value = false;
          }
        }, 500);
        return;
      }

      // No consent needed; the blank popup opened for it has nothing to show.
      popup?.window.close();
      await fetchOnboardingStatus(uuid);
      toastStore.displayToast({
        title: t("success"),
        message: t("journaling-rule-created"),
        duration: 5000,
        variant: "success",
        testId: "journaling-rule-connection-toast-success",
      });
      isJournalRuleRequestPending.value = false;
    } catch (e) {
      popup?.window.close();
      console.error(e);
      journalRuleError.value = true;
      journalRuleErrorMessage.value = e instanceof Error ? e.message : "";
      isJournalRuleRequestPending.value = false;
    }
  };
  const onConfigureJournalRule = () =>
    configureJournalRule(resolveExchangeOrgUuid());

  const updateNdr = (uuid: string) =>
    $http.put<{ url?: string }>(exchangeOnboardingUrl(uuid, "ndr-address"));

  // `afterConsent`: the request that completes a consent just granted. It
  // never needs a popup, so none is opened for it.
  const ndrFlow: ExchangeConsentFlow = {
    handled: ndrConsentHandled,
    error: ndrError,
    errorMessage: ndrErrorMessage,
    pending: isNdrRequestPending,
  };

  // `afterConsent`: the request that completes a consent just granted. It
  // never needs a popup, so none is opened for it; `attempt` counts its
  // retries while the API has not yet seen that consent.
  const configureNdr = async (
    uuid: string,
    afterConsent = false,
    attempt = 0,
  ) => {
    if (!uuid) return;

    // Clear any error from a previous attempt up front. The success path below
    // never reset it, so a retry that succeeds directly (no fresh consent
    // popup) left a stale error visible alongside the "Active" status. The
    // catch block re-sets it if this attempt also fails.
    ndrError.value = false;
    ndrErrorMessage.value = "";
    // Drop any stale consent result so it cannot leak into this attempt.
    clearConsentResult();
    isNdrRequestPending.value = true;
    // EMBEDDED: open the (blank, tagged) popup BEFORE the request, while the
    // click still counts as user activation (remote/consentPopup.ts). Null
    // means the browser blocked it; the standalone code below is never the
    // fallback for that.
    const embedded = isEmbedded();
    const popup =
      embedded && !afterConsent
        ? openConsentPopup({ placeholder: t("loading") })
        : null;
    try {
      const res = await updateNdr(uuid);
      const consentUrl = res.data?.url;

      if (consentUrl) {
        ndrConsentHandled.value = false;
        if (embedded) {
          if (afterConsent) {
            // Granted a moment ago but not yet visible to the API: try again
            // shortly, a few times.
            if (attempt < CONSENT_PROPAGATION_ATTEMPTS) {
              await consentPropagationDelay();
              await configureNdr(uuid, true, attempt + 1);
              return;
            }
            failExchangeConsent(ndrFlow);
            return;
          }
          if (!popup) {
            failExchangeConsent(ndrFlow);
            return;
          }
          const next = await runEmbeddedExchangeConsent(
            popup,
            consentUrl,
            ndrFlow,
            uuid,
          );
          if (next === "complete") await configureNdr(uuid, true);
          return;
        }
        const strWindowFeatures =
          "location=yes,height=570,width=520,scrollbars=yes,status=yes";
        const popupWindow = window.open(
          consentUrl,
          "_blank",
          strWindowFeatures,
        );

        const checkIfPopupWindowClosed = setInterval(async () => {
          if (!popupWindow?.closed) return;
          clearInterval(checkIfPopupWindowClosed);
          // The consent flow continues via the BroadcastChannel 'success'
          // message (which re-invokes this function). If the window was
          // closed without that message, do NOT re-open it. Re-check the real
          // state instead, in case consent succeeded but the broadcast was
          // lost — this is a read-only status fetch, so it cannot loop.
          if (ndrConsentHandled.value) return;
          ndrConsentHandled.value = true;
          try {
            const result = readConsentResult();
            if (result && result.success === false) {
              // The consent popup reported a failure. Surface it even when the
              // BroadcastChannel message never reached us (it can be lost when
              // the popup auto-closes right after posting).
              ndrError.value = true;
              ndrErrorMessage.value =
                result.errorDescription || result.error || "";
            } else {
              await fetchOnboardingStatus(uuid);
            }
          } catch (e) {
            console.error(e);
          } finally {
            clearConsentResult();
            isNdrRequestPending.value = false;
          }
        }, 500);
        return;
      }

      // No consent needed; the blank popup opened for it has nothing to show.
      popup?.window.close();
      await fetchOnboardingStatus(uuid);
      toastStore.displayToast({
        title: t("success"),
        message: t("ndr-email-address-created"),
        duration: 5000,
        variant: "success",
        testId: "ndr-connection-toast-success",
      });
      isNdrRequestPending.value = false;
    } catch (e) {
      popup?.window.close();
      console.error(e);
      ndrError.value = true;
      isNdrRequestPending.value = false;
    }
  };
  const onConfigureNdr = () => configureNdr(resolveExchangeOrgUuid());

  onBeforeUnmount(() => {
    channel.close();
    ndrChannel.close();
  });

  return {
    isRequestPending,
    pendingM365App,
    isOnLegacyApp,
    isOnNewApp,
    needsAppUpdate,
    primaryConnectApp,
    canShowNdrAndJournal,
    connectedAzureApp,
    m365UpdateStatus,
    isNdrRequestPending,
    isJournalRuleRequestPending,
    isM365Connected,
    isPhishServiceActive,
    isSatServiceActive,
    isPhsServiceActive,
    formattedM365LastUpdate,
    selectedCustomer,
    onConnectM365,
    onConnectOldAppM365,
    onUpdateM365Permissions,
    onConfigureNdr,
    onConfigureJournalRule,
    ndrError,
    ndrErrorMessage,
    journalRuleError,
    journalRuleErrorMessage,
    formattedJournalRuleLastUpdate,
    onboardingStatus,
    fetchOnboardingStatus,
    isNdrConfigured,
    isJournalRuleConfigured,
    formattedNdrLastUpdate,
    initialLoadComplete,
  };
}
